import { useEffect, useRef, useState, Dispatch, SetStateAction } from "react";
import { useScrollPosition } from "@/lib/hooks/use-scroll-position";

type UseScrollLockProps = {
  /**
   * If specified, window.scrollTo will be invoked with these options before the scroll is locked
   */
  scrollToOptions?: ScrollToOptions;
};

export const useScrollLock = ({ scrollToOptions }: UseScrollLockProps = {}): {
  scrollLocked: boolean;
  setScrollLocked: Dispatch<SetStateAction<boolean>>;
} => {
  const [scrollLocked, setScrollLocked] = useState<boolean>(false);
  const fixedScrollY = useRef<number>(
    typeof window !== "undefined" ? window.scrollY : 0,
  );

  const { windowScrollY, setIsListening } = useScrollPosition(false);

  const isMounted = useRef<boolean>(false);

  const lockScroll = (): void => {
    document.body.style.overflow = "hidden";
    fixedScrollY.current = window.scrollY;
    document.body.style.top = `-${fixedScrollY.current}px`;
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
  };

  const unlockScroll = (): void => {
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
    window.scrollTo(0, fixedScrollY.current);
    setIsListening(false);
  };

  useEffect(() => {
    if (!isMounted.current) {
      return;
    }
    if (!scrollLocked) {
      unlockScroll();
      return;
    }
    if (scrollToOptions && window.scrollY > 0) {
      setIsListening(true);
      window.scrollTo(scrollToOptions);
    } else {
      lockScroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollLocked]);

  useEffect(() => {
    if (!scrollToOptions || !isMounted.current) {
      return;
    }
    if (windowScrollY === 0) {
      setIsListening(false);
      lockScroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowScrollY]);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      unlockScroll();
      setIsListening(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { scrollLocked, setScrollLocked };
};
